import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { Building2, MapPin, Check, ChevronsUpDown } from "lucide-react";
import { Popover } from "radix-ui";
import { useAdminOrgStore } from "@/stores/admin-org";
import { useWasteFiltersStore } from "@/stores/waste-filters";
import { useClaims } from "@/hooks/use-claims";
import { useApiHeaders } from "@/hooks/use-api-headers";
import { useTranslation } from "@/i18n/use-translation";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

interface Org {
  id: string;
  name: string;
}

interface Site {
  id: string;
  name: string;
}

export function ContextPicker() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const headers = useApiHeaders();
  const { isPlatformAdmin, orgId: claimsOrgId } = useClaims();
  const { orgId, orgName, siteId, siteName, setOrg, setSite, clearSite } = useAdminOrgStore();

  const effectiveOrgId = isPlatformAdmin ? orgId : claimsOrgId;

  // Platform admin: fetch all orgs
  const orgsQuery = useQuery<Org[]>({
    queryKey: ["admin", "organisations"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/admin/organisations`, { headers });
      if (!res.ok) throw new Error("Failed to load organisations");
      return (await res.json()).data;
    },
    enabled: isPlatformAdmin && !!headers.Authorization.replace("Bearer ", ""),
  });

  // Org admin: fetch own org name
  const myOrgQuery = useQuery<Org>({
    queryKey: ["organisation"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/organisations/me`, { headers });
      if (!res.ok) throw new Error("Failed to load organisation");
      return (await res.json()).data;
    },
    enabled: !isPlatformAdmin && !!claimsOrgId && !!headers.Authorization.replace("Bearer ", ""),
  });

  // Fetch sites for the effective org
  const sitesQuery = useQuery<Site[]>({
    queryKey: ["context-sites", effectiveOrgId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/sites`, { headers });
      if (!res.ok) throw new Error("Failed to load sites");
      return (await res.json()).data;
    },
    enabled: !!effectiveOrgId && !!headers.Authorization.replace("Bearer ", ""),
  });

  const orgs = orgsQuery.data ?? [];
  const sites = sitesQuery.data ?? [];
  const displayOrgName = isPlatformAdmin
    ? orgName
    : (myOrgQuery.data?.name ?? t("nav.organisation"));

  // Build trigger label
  let triggerLabel = t("nav.selectOrg");
  if (effectiveOrgId) {
    triggerLabel = displayOrgName;
    if (siteName) triggerLabel += ` \u203A ${siteName}`;
  }

  function handleSelectOrg(org: Org) {
    setOrg(org.id, org.name);
    setOpen(false);
    // Drop the persisted waste filters — a site_id from the previous org would 404 /
    // return no data on the next page (POS2-967). The org-change reset in
    // useWasteFilters only fires while that hook is mounted (not on DataBridge), so
    // clear it here at the switch point.
    useWasteFiltersStore.getState().clearShared();
    // Clear cache — re-render will create fresh queries with updated X-Org-Id header
    queryClient.removeQueries();
  }

  function handleSelectSite(site: Site) {
    setSite(site.id, site.name);
    setOpen(false);
    queryClient.removeQueries();
  }

  function handleAllSites() {
    clearSite();
    setOpen(false);
    queryClient.removeQueries();
  }

  return (
    <div className="group-data-[collapsible=icon]:hidden">
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            className="flex h-8 w-full items-center gap-1.5 rounded-md px-2 text-xs text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            aria-label={t("nav.switchContext")}
          >
            <Building2 className="size-3.5 shrink-0" />
            <span className="flex-1 truncate text-left">{triggerLabel}</span>
            <ChevronsUpDown className="size-3 shrink-0 opacity-50" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            side="bottom"
            align="start"
            sideOffset={4}
            className="z-50 w-[240px] rounded-md border bg-popover p-0 shadow-md animate-in fade-in-0 zoom-in-95"
          >
            <Command>
              <CommandInput placeholder={t("common.searchPlaceholder")} />
              <CommandList>
                <CommandEmpty>{t("common.noResults")}</CommandEmpty>

                {isPlatformAdmin ? (
                  <>
                    {orgs.map((org) => (
                      <CommandGroup key={org.id} heading={org.name}>
                        {org.id === orgId && (
                          <>
                            <CommandItem
                              value={`all-sites-${org.id}`}
                              onSelect={handleAllSites}
                              className="text-xs"
                            >
                              <Building2 className="size-3.5" />
                              <span>{t("nav.allSites")}</span>
                              {!siteId && <Check className="ml-auto size-3.5" />}
                            </CommandItem>
                            {sites.map((site) => (
                              <CommandItem
                                key={site.id}
                                value={`site-${site.id}-${site.name}`}
                                onSelect={() => handleSelectSite(site)}
                                className="text-xs"
                              >
                                <MapPin className="size-3.5" />
                                <span>{site.name}</span>
                                {site.id === siteId && <Check className="ml-auto size-3.5" />}
                              </CommandItem>
                            ))}
                          </>
                        )}
                        {org.id !== orgId && (
                          <CommandItem
                            value={`org-${org.id}-${org.name}`}
                            onSelect={() => handleSelectOrg(org)}
                            className="text-xs"
                          >
                            <Building2 className="size-3.5" />
                            <span>{org.name}</span>
                            {org.id === orgId && <Check className="ml-auto size-3.5" />}
                          </CommandItem>
                        )}
                      </CommandGroup>
                    ))}
                  </>
                ) : (
                  <CommandGroup heading={displayOrgName}>
                    <CommandItem
                      value={`all-sites-${effectiveOrgId}`}
                      onSelect={handleAllSites}
                      className="text-xs"
                    >
                      <Building2 className="size-3.5" />
                      <span>{t("nav.allSites")}</span>
                      {!siteId && <Check className="ml-auto size-3.5" />}
                    </CommandItem>
                    {sites.map((site) => (
                      <CommandItem
                        key={site.id}
                        value={`site-${site.id}-${site.name}`}
                        onSelect={() => handleSelectSite(site)}
                        className="text-xs"
                      >
                        <MapPin className="size-3.5" />
                        <span>{site.name}</span>
                        {site.id === siteId && <Check className="ml-auto size-3.5" />}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}
